import { errorMessage, phoneSchema } from "@workspace/domain/src/utils";
import dayjs from "@workspace/domain/src/utils/dayjs";
import Decimal from "decimal.js";
import * as z from "zod";

const ClientSchema = z.object({
  id: z.string(),

  agencyId: z.string(),
  clientId: z.string().nullable(),

  name: z.string(),
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  emailVerified: z.boolean().nullish(),
  phoneVerified: z.boolean().nullish(),
  imageUrl: z.string().nullish(),

  totalBookings: z.number(),
  totalSpent: z.number(),
  totalAmountDue: z.number(),
  lastTripDate: z.date().nullable(),
  notes: z.string().nullable(),
  tags: z.string().array(),

  encryptionKey: z.string().nullable(),
  nIdFrontKey: z.string().nullable(),
  nIdFrontIv: z.string().nullable(),
  nIdBackKey: z.string().nullable(),
  nIdBackIv: z.string().nullable(),

  createdAt: z.date(),
  updatedAt: z.date(),
});

const SortBy = [
  "name",
  "phoneNumber",
  "totalBookings",
  "totalSpent",
  "lastTripDate",
  "totalAmountDue",
  "createdAt",
] as const;
const SortOrder = ["asc", "desc"] as const;

const GetSchema = z.object({
  id: z.string(),
});

const GetAllSchema = z
  .object({
    page: z.number(),
    limit: z.number(),

    sortBy: z.enum(SortBy),
    sortOrder: z.enum(SortOrder),
  })
  .extend(
    z
      .object({
        search: z.string(),

        totalSpentMin: z.number().transform((val) => new Decimal(val).times(100).toNumber()),
        totalSpentMax: z.number().transform((val) => new Decimal(val).times(100).toNumber()),

        totalBookingsMin: z.number(),
        totalBookingsMax: z.number(),

        lastTripDateMin: z.union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()]),
        lastTripDateMax: z.union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()]),
        tags: z.string().array(),
      })
      .partial().shape,
  );

const BaseSchema = z.object({
  name: z
    .string(errorMessage("Name is required", "Name is invalid"))
    .nonempty("Name is required")
    .max(255, "Name must be less than 255 characters"),
  phoneNumber: phoneSchema("Phone number is required", "Phone number is invalid"),
  email: z.union([z.email(errorMessage("Email is required", "Email is invalid")), z.literal("")]).optional(),

  notes: z.string(errorMessage("Notes are required", "Notes are invalid")).optional(),
  tags: z
    .array(
      z.string(errorMessage("Tag is required", "Tag is invalid")),
      errorMessage("Tags are required", "Tags are invalid"),
    )
    .transform((arr) => arr.filter((str) => str.trim() !== "")),

  force: z.boolean().optional(),
});

const CreateSchema = BaseSchema;

const UpdateSchema = BaseSchema.extend({
  id: z.string(),
});

export const AdminClient = {
  Client: ClientSchema,
  Get: GetSchema,
  GetAll: GetAllSchema,
  Create: CreateSchema,
  Update: UpdateSchema,
  SortBy: SortBy,
  SortOrder: SortOrder,
};

export type AdminClient = {
  Client: z.infer<typeof ClientSchema>;
  Get: z.infer<typeof GetSchema>;
  GetAll: z.infer<typeof GetAllSchema>;
  Create: z.infer<typeof CreateSchema>;
  Update: z.infer<typeof UpdateSchema>;
  SortBy: (typeof SortBy)[number];
  SortOrder: (typeof SortOrder)[number];
};
